import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TabPanel, TabView } from 'primereact/tabview';
import { AutoComplete } from 'primereact/autocomplete';
import MailList from './Components/MailList';
import MailPreview from './Components/MailPreview';
import MailCreateModal from './Components/MailCreateModal';
import useEmails from './hooks/useEmail';
import useEmailDetails from './hooks/useEmailDetails';
import './mailbox.scss';
import { fetchFiles } from 'services/fileManager/fileManagerServices';
import { useDebounce } from 'hooks/useDebounce';
import CaseMappingModal from './reconciliation/Components/CaseMappingModal';
import ReconcileHeader from './reconciliation/Components/ReconcileHeader';
import { convertToTimezoneWithStartOfDayFormat, convertToTimezoneWithEndOfDayFormat } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import { MAPPING_STATUSES } from 'constants/mappingStatus';

const MailBox = ({ caseDetails }) => {
  const caseId = caseDetails?.case_id;
  const caseName = caseDetails?.case_description?.case_name;
  const isLead = caseDetails?.is_lead ? caseDetails?.is_lead : false;
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedEmailThreadPkSk, setSelectedEmailThreadPkSk] = useState({
    pk: null,
    sk: null,
  });
  const [show, setShow] = useState(false);
  const [readEmails, setReadEmails] = useState({});
  const [existingFiles, setExistingFiles] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showEmailMapping, setShowEmailMapping] = useState(false);
  const [dateFilter, setDateFilter] = useState('');
  const defaultSelection = MAPPING_STATUSES.filter((status) => status.key !== 5).map((status) => status.value);
  const [filters, setFilters] = useState({
    caseId: caseId,
    mail_date_lower: '',
    mail_date_upper: '',
    mappingStatuses: defaultSelection,
    userFilter: [],
  });

  const userContext = useContext(UserDetailsContext);
  const { emailDetails, setEmailDetails } = useEmailDetails(selectedEmailThreadPkSk, setReadEmails);

  const pickExtractedDate = (dateArray) => {
    return {
      mail_date_lower: dateArray?.[0] ? convertToTimezoneWithStartOfDayFormat(dateArray[0], userContext?.userDetails.timezone) : '',
      mail_date_upper: dateArray?.[0]
        ? convertToTimezoneWithEndOfDayFormat(dateArray[1] ? dateArray[1] : dateArray[0], userContext?.userDetails.timezone)
        : '',
    };
  };

  useEffect(() => {
    const { mail_date_lower, mail_date_upper } = pickExtractedDate(dateFilter);
    setFilters((prev) => ({
      ...prev,
      mail_date_lower,
      mail_date_upper,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const {
    emails,
    setEmails,
    handleLoadMore,
    loading: emailLoading,
    hasNext,
    paginationLoading,
    refreshEmails,
  } = useEmails(searchKeyword, setReadEmails, activeIndex === 1, filters, 20);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchKeyword(e.target.value);
    }
  };

  const handleEmailData = (data) => {
    setEmailDetails((prev) => [...prev, data]);
  };

  const handleSentEmailList = (data) => {
    if (activeIndex === 1) {
      setEmails((prev) => [data, ...prev]);
    }
  };

  const tabs = [
    { header: 'Inbox', filter: (email) => email },
    { header: 'Sent', filter: (email) => email?.is_sent },
  ];

  const debouncedQuery = useDebounce(query, 500);

  const getFiles = (obj) => {
    fetchFiles(obj)
      .then(async (response) => {
        let arr = response?.data?.data;
        const filesData = arr
          ?.filter((data) => data.entry_type === 'file')
          .map((item) => {
            return {
              ...item,
              label: item.display_name,
              value: {
                file_path: item.file_path,
                size: item.size,
                display_name: item.display_name,
                file_name: item.file_name,
                file_type: item.file_type,
                created_by: item.created_by_details,
                is_new_file: true,
                sk: item.sk,
                pk: item.pk,
              },
            };
          });
        setExistingFiles((prev) => {
          if (prev.length > 0 && prev[0]?.case_id !== obj.case_id) {
            return filesData;
          }

          const nonListedItems = [];
          for (const data of filesData) {
            const ifNotAdded = prev.find((prv) => prv?.sk === data?.sk);
            if (!ifNotAdded) {
              nonListedItems.push(data);
            }
          }
          return [...prev, ...nonListedItems];
        });
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (!caseDetails?.case_id) return;

    const obj = {
      case_id: caseDetails.case_id,
      selectedFolder: '',
      selectedType: '',
      tag: '',
      query: debouncedQuery,
      pageLimit: 10,
      pagination_direction: '',
      is_read: '',
    };

    getFiles(obj);
  }, [caseDetails?.case_id, debouncedQuery]);

  useEffect(() => {
    const dropdownClasses = document?.querySelectorAll('.p-multiselect-token');
    for (const dropdownElement of dropdownClasses) {
      const dropLabelElement = dropdownElement?.querySelector('.p-multiselect-token-label')?.innerHTML;

      if (dropLabelElement?.trim()?.length === 0) {
        dropdownElement.style.display = 'none';
      }
    }
  }, [existingFiles]);

  return (
    <>
      {/* <div className="d-flex py-2">
        <Button
          type="button"
          className="btn-outline p-button-primary ms-2 mb-2"
          label="Map to Case/Lead"
          disabled={!selectedEmails?.length > 0}
          onClick={() => setShowEmailMapping(true)}
        />
        <Button
          label="Compose"
          type="button"
          className="btn-outline p-button-primary ms-auto mb-2"
          icon="pi pi-plus"
          onClick={() => setShow(true)}
        />
      </div> */}
      <ReconcileHeader
        isGlobalMail={false}
        caseValue={caseId}
        dateFilter={dateFilter}
        onDateChange={setDateFilter}
        refreshEmails={refreshEmails}
        setShowComposeModal={setShow}
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
        setShow={setShowEmailMapping}
        setSelectedEmailThreadPkSk={setSelectedEmailThreadPkSk}
        filters={filters}
        setFilters={setFilters}
        mapping_statuses={defaultSelection}
      />
      <div className="d-flex align-items-center mb-1 mx-2">
        <span className="d-flex p-1 align-items-center" style={{ lineHeight: '18px', fontSize: '12px' }}>
          <i className="pi pi-info-circle me-2"></i>
          AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent verification.
        </span>
      </div>

      <div className="d-flex row mail-list-tab-view mb-3 max-height-90">
        <div className="col-lg-4 col-12 mail-list-section">
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => {
              setActiveIndex(e.index);
              setSelectedEmailThreadPkSk({ pk: null, sk: null });
            }}
            className="card-bg tab-view h-100"
          >
            {tabs.map((tab) => (
              <TabPanel header={tab.header} key={`tab-${tab.header}`}>
                <div>
                  <span className="p-input-icon-left w-100">
                    <AutoComplete
                      inputClassName="email-search w-100"
                      className="input-shadow w-100"
                      value={searchKeyword}
                      onChange={(e) => {
                        setSearchKeyword(e.target.value);
                        setSelectedEmailThreadPkSk({});
                      }}
                      placeholder="Search"
                      onKeyPress={(e) => handleSearchKeyPress(e)}
                      autoComplete="off"
                    />
                    {/* #todo: add reconciliation button for mapping wrongly mapped emails */}
                  </span>
                </div>
                {emailLoading ? (
                  <div className="d-flex justify-content-center align-items-center mt-3">
                    <i className="pi pi-spin pi-spinner me-2" />
                    Loading {tab.header} Emails...
                  </div>
                ) : (
                  <MailList
                    allMails={emails}
                    selectedEmailThreadPk={selectedEmailThreadPkSk?.pk}
                    setSelectedEmailThreadPkSk={setSelectedEmailThreadPkSk}
                    handleLoadMore={handleLoadMore}
                    hasNext={hasNext}
                    paginationLoading={paginationLoading}
                    readEmails={readEmails}
                    setReadEmails={setReadEmails}
                    multiple
                    loading={false}
                    selectedEmails={selectedEmails}
                    setSelectedEmails={setSelectedEmails}
                  />
                )}
              </TabPanel>
            ))}
          </TabView>
        </div>

        <div className="flex-1 col-lg-8 col-12  mb-lg-0 mb-5">
          {show && (
            <MailCreateModal
              show={show}
              setShow={setShow}
              caseId={caseId}
              isLead={isLead}
              caseName={caseName}
              handleSentEmailList={handleSentEmailList}
              className="card-bg"
            />
          )}
          {selectedEmailThreadPkSk?.pk ? (
            <MailPreview
              previewMail={emailDetails}
              caseDetails={caseDetails}
              existingFiles={existingFiles}
              setQuery={setQuery}
              handleEmailData={handleEmailData}
            />
          ) : (
            <div className="card-bg p-3 h-100">
              <div className="d-flex flex-column align-items-center justify-content-center h-100 F-size16 start-conversation">
                {/* <i className="icon-chat" /> */}
                <i className="bi bi-envelope" />
                <span>Select an Email</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <CaseMappingModal
        show={showEmailMapping}
        setShow={setShowEmailMapping}
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
        setSelectedEmailThreadPkSk={setSelectedEmailThreadPkSk}
        refreshEmails={refreshEmails}
        handleReconcile={refreshEmails}
      />
    </>
  );
};

MailBox.propTypes = {
  caseDetails: PropTypes.shape({
    case_id: PropTypes.string.isRequired,
  }).isRequired,
};

export default MailBox;
